/**
 * UploadProgressItem Component (AC-809)
 *
 * Displays individual file upload progress with status indicators.
 * Supports states: pending, uploading, processing, completed, failed.
 *
 * Technical Design Reference: docs/technical/TDD-AC-804-knowledge-base-file-upload.md
 */

import React from 'react';
import { Clock, Upload, Loader, CheckCircle, XCircle } from 'lucide-react';

export type UploadStatus = 'pending' | 'uploading' | 'processing' | 'completed' | 'failed';

interface UploadProgressItemProps {
  filename: string;
  status: UploadStatus;
  progress: number;
  error?: string;
  chunksCreated?: number;
  replaced?: boolean;
  chunksRemoved?: number;
}

const UploadProgressItem: React.FC<UploadProgressItemProps> = ({
  filename,
  status,
  progress,
  error,
  chunksCreated,
  replaced,
  chunksRemoved,
}) => {
  const getStatusIcon = () => {
    switch (status) {
      case 'pending':
        return (
          <span data-testid="status-icon-pending" data-icon-type="Clock">
            <Clock className="w-5 h-5 text-gray-400" />
          </span>
        );
      case 'uploading':
        return (
          <span data-testid="status-icon-uploading" data-icon-type="Upload">
            <Upload className="w-5 h-5 text-primary-500 animate-pulse" />
          </span>
        );
      case 'processing':
        return (
          <span data-testid="status-icon-processing" data-icon-type="Loader" data-testid-spinner="processing-spinner">
            <Loader className="w-5 h-5 text-primary-500 animate-spin" data-testid="processing-spinner" />
          </span>
        );
      case 'completed':
        return (
          <span data-testid="status-icon-completed" data-icon-type="CheckCircle">
            <CheckCircle className="w-5 h-5 text-green-500" />
          </span>
        );
      case 'failed':
        return (
          <span data-testid="status-icon-failed" data-icon-type="XCircle">
            <XCircle className="w-5 h-5 text-red-500" />
          </span>
        );
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'pending':
        return 'Waiting...';
      case 'uploading':
        return `Uploading... ${progress}%`;
      case 'processing':
        return 'Processing...';
      case 'completed':
        if (chunksCreated !== undefined && chunksCreated >= 0) {
          const chunkWord = chunksCreated === 1 ? 'chunk' : 'chunks';
          if (replaced && chunksRemoved !== undefined && chunksRemoved > 0) {
            return `Completed - ${chunksCreated} ${chunkWord} created (replaced ${chunksRemoved} old)`;
          }
          return `Completed - ${chunksCreated} ${chunkWord} created`;
        }
        return 'Completed';
      case 'failed':
        return 'Failed';
    }
  };

  const getContainerClass = () => {
    const baseClasses = 'flex items-start p-3 rounded-lg border';

    switch (status) {
      case 'completed':
        return `${baseClasses} success bg-green-50 border-green-200`;
      case 'failed':
        return `${baseClasses} error bg-red-50 border-red-200`;
      case 'uploading':
      case 'processing':
        return `${baseClasses} active bg-primary-50 border-blue-200`;
      default:
        return `${baseClasses} bg-gray-50 dark:bg-gray-900 border-gray-200 dark:border-gray-700`;
    }
  };

  const getAriaLive = (): 'polite' | 'assertive' | 'off' => {
    if (status === 'failed') return 'assertive';
    if (status === 'completed') return 'polite';
    return 'off';
  };

  const getAriaLabel = () => {
    return `${filename} - ${status} - ${progress}%`;
  };

  const showProgressBar = status === 'uploading' || status === 'processing';

  return (
    <div
      data-testid="upload-progress-item"
      className={getContainerClass()}
      aria-label={getAriaLabel()}
      aria-live={getAriaLive()}
    >
      <div className="flex-shrink-0 mr-3">{getStatusIcon()}</div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-warm-white truncate">{filename}</p>

        <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{getStatusText()}</p>

        {/* Progress bar for uploading/processing */}
        {showProgressBar && (
          <div className="mt-2">
            <div
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              className="w-full bg-gray-200 rounded-full h-2 overflow-hidden"
            >
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  status === 'processing' ? 'bg-blue-400 animate-pulse' : 'bg-blue-500'
                }`}
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{progress}%</p>
          </div>
        )}

        {/* Error message for failed uploads */}
        {status === 'failed' && (
          <p className="text-sm text-red-600 mt-1">
            {error || 'Failed to process file'}
          </p>
        )}
      </div>
    </div>
  );
};

export default UploadProgressItem;
